import { CmpMemberLifecycleStatusEnum } from "../../../../graphql/types";
import {
  getUserFacingErrorText,
  NETWORK_ERROR_TEXT,
  STAFF_SUPPORT_EMAIL,
} from "../../../../utils/userFacingError";
import { getPreserveExpectedErrorsForCallerContext } from "../../../../utils/apolloErrorPolicy";

// The contact access actions send their expected refusals (stale page,
// status, shared or in-use email, test inbox) as BAD_USER_INPUT. The card
// shows them, so they are not reported to Sentry; other failures still are
// (Sentry CHAMBER-MEMBER-PRO-6B, Oct 30 2026).
export const contactAccessMutationApolloContext =
  getPreserveExpectedErrorsForCallerContext({ expectedGraphQLErrorCodes: ["BAD_USER_INPUT"] });

export const getMemberCredentialLifecycleBlock = (lifecycleStatus: CmpMemberLifecycleStatusEnum): string =>
  lifecycleStatus === CmpMemberLifecycleStatusEnum.Active
    ? ""
    : "You can send Member Dashboard credentials only to Active members. Make this member Active first.";

export type MemberCredentialAction = "download" | "send";

// Update Member Form design copy for every credential send or download pop-up.
export const credentialFollowUpNote = "After sending, monitor the member’s login status under Manage Members. If they have not accessed their Member Dashboard, follow up to confirm they received the email. Ask them to check their spam or junk folder and verify their email address before resending.";

type PersistedMemberLogin = {
  adminConsoleLoginEmail?: string | null;
};

// Raw text is read only to route the error to a fixed message. It is never
// shown as it is: the screen text comes from getUserFacingErrorText.
const getErrorMessage = (error: unknown): string => {
  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof (error as { message?: unknown }).message === "string"
  ) {
    return (error as { message: string }).message.trim();
  }

  return "";
};

export const normalizeMemberLoginEmail = (
  email?: string | null
): string => (email || "").trim().toLowerCase();

/**
 * Credential actions must use the login email from the loaded member record.
 * An unsaved contact edit or a directory-email fallback is not an identity
 * source and must never redirect a password or credential email operation.
 */
export const getPersistedMemberLoginEmail = (
  member?: PersistedMemberLogin | null
): string => normalizeMemberLoginEmail(member?.adminConsoleLoginEmail);

/**
 * Approval of a website application only checks the email that activation
 * will give a dashboard login. The Administrator contact is optional on Join
 * Now (Claude Design); without it, activation gives the saved Primary Contact
 * the login (CIBC Wood Gundy, Ajax, Oct 15 2026). Nothing is saved here.
 */
export const getWebsiteApprovalLoginEmail = ({
  savedLoginEmail,
  primaryContactEmail,
}: {
  savedLoginEmail?: string | null;
  primaryContactEmail?: string | null;
}): string =>
  normalizeMemberLoginEmail(savedLoginEmail) ||
  normalizeMemberLoginEmail(primaryContactEmail);

export const hasUnsavedMemberLoginEmailChange = ({
  editableLoginEmail,
  persistedLoginEmail,
}: {
  editableLoginEmail?: string | null;
  persistedLoginEmail?: string | null;
}): boolean =>
  normalizeMemberLoginEmail(editableLoginEmail) !==
  normalizeMemberLoginEmail(persistedLoginEmail);

export const getLatestCredentialSentAtUtcMilli = ({
  confirmedCredentialsSentAtUtcMilli,
  persistedCredentialsSentAtUtcMilli,
}: {
  confirmedCredentialsSentAtUtcMilli?: number | null;
  persistedCredentialsSentAtUtcMilli?: number | null;
}): number | null => {
  const timestamps = [
    confirmedCredentialsSentAtUtcMilli,
    persistedCredentialsSentAtUtcMilli,
  ].filter(
    (timestamp): timestamp is number =>
      typeof timestamp === "number" &&
      Number.isFinite(timestamp) &&
      timestamp > 0
  );

  return timestamps.length > 0 ? Math.max(...timestamps) : null;
};

/**
 * The backend refuses a login email that already signs in to another Member
 * Dashboard account before it changes anything ("No credentials were changed
 * or sent"), so staff can enter another email and try again at once.
 */
export const isMemberLoginEmailInUseRefusal = (error: unknown): boolean => {
  const normalizedMessage = getErrorMessage(error).toLowerCase();
  return normalizedMessage.includes("a dashboard account with the requested login email already exists") &&
    normalizedMessage.includes("no credentials were changed or sent");
};

// Monday 13387351653: the Enable refusal for a contact's access, with the
// real field and button labels.
export const contactLoginEmailInUseMessage =
  "This email already signs in to a Member Dashboard account. Enter a different Contact Email for this contact, then click Save Changes.";

// The other contact-email refusals the backend makes before any change
// (memberDashboardChamberLinks.ts, cmpMemberContactAccounts.ts). Their text
// already says to enter a different email (live Oct 30 2026: same chamber).
const contactEmailRetryRefusals = [
  "already signs in to another member's dashboard in your chamber",
  "has a member dashboard login in another chamber, but that login is turned off",
  "who already has member dashboard access. each contact needs a unique login email",
  "this contact is the same as the primary contact, who already has member dashboard access",
];

export const isContactEmailRetryRefusal = (error: unknown): boolean => {
  const normalizedMessage = getErrorMessage(error).toLowerCase();
  return contactEmailRetryRefusals.some((text) => normalizedMessage.includes(text));
};

export const getMemberCredentialErrorMessage = ({
  action,
  error,
}: {
  action: MemberCredentialAction;
  error: unknown;
}): string => {
  const message = getErrorMessage(error);
  const normalizedMessage = message.toLowerCase();
  // Server text (no Apollo wrapper) or a UserFacingError message; "" for
  // client guard text and other errors.
  const serverText = getUserFacingErrorText(error, "");

  if (
    action === "send" &&
    normalizedMessage.includes("temporary password was updated") &&
    (normalizedMessage.includes("email was not sent") ||
      normalizedMessage.includes("no credentials were sent"))
  ) {
    return serverText && serverText !== NETWORK_ERROR_TEXT
      ? serverText
      : "The member's temporary password was updated, but the credentials email was not sent. Select Send Credentials again or use Download Credentials.";
  }

  if (isMemberLoginEmailInUseRefusal(error)) {
    return action === "send"
      ? "The credentials email was not sent. This sign-in email is already used for another Member Dashboard. Go to Step 2 Member Contacts, enter a different Contact Email under Member Administrator Contact, and select Save Changes. Then try again."
      : "Credentials were not downloaded, and no email was sent. This sign-in email is already used for another Member Dashboard. Go to Step 2 Member Contacts, enter a different Contact Email under Member Administrator Contact, and select Save Changes. Then try again.";
  }

  if (
    normalizedMessage.includes("not authorized for this resource") ||
    // Backend wording from Oct 19 2026 for the same refusal.
    normalizedMessage.includes("you do not have permission to do this") ||
    normalizedMessage.includes("dashboard identity") ||
    normalizedMessage.includes("dashboard login account") ||
    normalizedMessage.includes("dashboard account")
  ) {
    return action === "send"
      ? `The credentials email was not sent. We could not check this member’s Member Dashboard sign-in. Refresh the page and try again. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`
      : `Credentials were not downloaded, and no email was sent. We could not check this member’s Member Dashboard sign-in. Refresh the page and try again. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`;
  }

  if (
    serverText === NETWORK_ERROR_TEXT ||
    normalizedMessage.includes("failed to fetch") ||
    normalizedMessage.includes("network")
  ) {
    return action === "send"
      ? "We could not confirm that the credentials email was sent. Refresh the page and check Last Sent before you send it again."
      : "We could not confirm that credentials were created. No email was sent. Check your internet connection and try again.";
  }

  if (serverText) {
    return action === "send"
      ? `We could not confirm that the credentials email was sent. ${serverText}`
      : `Credentials were not downloaded. No email was sent. ${serverText}`;
  }

  return action === "send"
    ? `We could not confirm that the credentials email was sent. Refresh the page and check Last Sent before you send it again. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`
    : `Credentials were not downloaded. No email was sent. Refresh the page and try again. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`;
};

export const getCredentialRefreshWarning = ({
  action,
}: {
  action: MemberCredentialAction;
}): string =>
  action === "send"
    ? "The email was confirmed as sent, but the member status did not refresh automatically. Refresh this page to load the latest status."
    : "The credentials were downloaded, but the member status did not refresh automatically. Refresh this page to load the latest status.";

/**
 * A detail refresh is follow-up UI work, not part of the already-confirmed
 * credential operation. Resolve refresh failures so callers keep the
 * successful outcome visible.
 */
export const refreshMemberDetailAfterConfirmedCredentialAction = async ({
  onRefreshFailed,
  refetch,
}: {
  onRefreshFailed: () => void;
  refetch: () => Promise<unknown>;
}): Promise<boolean> => {
  try {
    await refetch();
    return true;
  } catch {
    onRefreshFailed();
    return false;
  }
};
